/**
 * TrainingFilters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { InlineFilterBar, InlineSelect } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import {
  TrainingNRType,
  TrainingNRTypeLabels,
  TrainingCategory,
  TrainingCategoryLabels,
  TrainingStatus,
  TrainingStatusLabels,
  TrainingValidityStatus,
  TrainingValidityStatusLabels,
} from '../../../types/training';

export interface TrainingFiltersData {
  searchTerm: string;
  type: string;
  category: string;
  validityStatus: string;
  status: string;
}

interface Props {
  onFilter: (filters: TrainingFiltersData) => void;
  loading?: boolean;
  total?: number;
}

// Options
const TYPE_OPTIONS = Object.entries(TrainingNRTypeLabels).map(([key, label]) => ({
  value: key,
  label,
}));
const CATEGORY_OPTIONS = Object.entries(TrainingCategoryLabels).map(([key, label]) => ({
  value: key,
  label,
}));
const VALIDITY_STATUS_OPTIONS = Object.entries(TrainingValidityStatusLabels).map(([key, label]) => ({
  value: key,
  label,
}));
const STATUS_OPTIONS = Object.entries(TrainingStatusLabels).map(([key, label]) => ({
  value: key,
  label,
}));

const defaultValues: TrainingFiltersData = {
  searchTerm: '',
  type: '',
  category: '',
  validityStatus: '',
  status: '',
};

export const TrainingFiltersComponent: React.FC<Props> = ({
  onFilter,
  loading = false,
  total,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      type: 'Tipo',
      category: 'Categoria',
      validityStatus: 'Vigência',
      status: 'Status',
    },
    formatValue: {
      type: (v: string) => TrainingNRTypeLabels[v as TrainingNRType] ?? v,
      category: (v: string) => TrainingCategoryLabels[v as TrainingCategory] ?? v,
      validityStatus: (v: string) => TrainingValidityStatusLabels[v as TrainingValidityStatus] ?? v,
      status: (v: string) => TrainingStatusLabels[v as TrainingStatus] ?? v,
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome, funcionário..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="treinamentos"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineSelect
        label="Tipo"
        value={filters.type}
        options={TYPE_OPTIONS}
        onChange={(v) => setFilter('type', v)}
        emptyLabel="Todos"
        minWidth={140}
      />
      <InlineSelect
        label="Categoria"
        value={filters.category}
        options={CATEGORY_OPTIONS}
        onChange={(v) => setFilter('category', v)}
        emptyLabel="Todas"
        minWidth={150}
      />
      <InlineSelect
        label="Vigência"
        value={filters.validityStatus}
        options={VALIDITY_STATUS_OPTIONS}
        onChange={(v) => setFilter('validityStatus', v)}
        emptyLabel="Todas"
        minWidth={150}
      />
      <InlineSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v)}
        emptyLabel="Todos"
        minWidth={140}
      />
    </InlineFilterBar>
  );
};

export default TrainingFiltersComponent;
